import { isBitmapReference } from "./leaflet-bitmaps";

/**
 * Leaflet pages are SVG artwork with the editor's document embedded as metadata,
 * so a page reopens exactly as it was edited. Pages without the metadata are blank.
 * Images refer to the branch's bitmaps rather than containing them, and QR codes
 * encode the leaflet's scan link.
 */

export const paperSizes = ["a5", "a4"] as const;
export type PaperSize = (typeof paperSizes)[number];
export const paperSizeLabels: Readonly<Record<PaperSize, string>> = { a5: "A5", a4: "A4" };

export const sides = ["front", "back"] as const;
export type Side = (typeof sides)[number];
export const sideLabels: Readonly<Record<Side, string>> = { front: "Front", back: "Back" };

export type Pages = Readonly<Record<Side, string>>;

/** The server rejects larger pages. */
export const MAXIMUM_PAGE_BYTES = 1024 * 1024;

const trimMillimetres: Readonly<Record<PaperSize, readonly [number, number]>> = {
  a5: [148, 210],
  a4: [210, 297],
};
const BLEED_MILLIMETRES = 3;
const SAFE_MARGIN_MILLIMETRES = 5;
export const POINTS_PER_MILLIMETRE = 72 / 25.4;
const METADATA_ID = "groundspark-editor";
const METADATA_END = "</metadata>";
/**
 * Version 2 added layer names and locks, which older editors would drop when saving;
 * version 3 added QR codes, which they cannot draw.
 */
const FORMAT_VERSION = 3;
/** Version 1 pages open as they are, with unnamed and unlocked layers. */
const READABLE_VERSIONS: ReadonlySet<unknown> = new Set([1, 2, FORMAT_VERSION]);

/** Portrait artwork including bleed, measured in points from its top-left corner. */
export interface PageGeometry {
  readonly width: number;
  readonly height: number;
  /** Physical SVG size, such as `154mm`. */
  readonly svgWidth: string;
  readonly svgHeight: string;
  /** Inset of the trim edge. */
  readonly bleed: number;
  /** Inset of the area that is safe from trimming. */
  readonly safe: number;
}

export function pageGeometry(paperSize: PaperSize): PageGeometry {
  const [trimWidth, trimHeight] = trimMillimetres[paperSize];
  const width = trimWidth + 2 * BLEED_MILLIMETRES;
  const height = trimHeight + 2 * BLEED_MILLIMETRES;
  return {
    width: width * POINTS_PER_MILLIMETRE,
    height: height * POINTS_PER_MILLIMETRE,
    svgWidth: `${width}mm`,
    svgHeight: `${height}mm`,
    bleed: BLEED_MILLIMETRES * POINTS_PER_MILLIMETRE,
    safe: (BLEED_MILLIMETRES + SAFE_MARGIN_MILLIMETRES) * POINTS_PER_MILLIMETRE,
  };
}

/** A Fabric canvas serialized by `Canvas.toObject`. */
export interface CanvasDocument {
  readonly objects: readonly Record<string, unknown>[];
  readonly [property: string]: unknown;
}

/**
 * The editor document is written as the root's first child. Artwork is never
 * parsed in the browser: its inline styles would be checked against the page's
 * content security policy.
 */
export function editableSvg(rendered: string, canvas: CanvasDocument): string {
  const rootEnd = rendered.startsWith("<svg ") ? rendered.indexOf(">") : -1;
  if (rootEnd < 0 || rendered[rootEnd - 1] === "/")
    throw new TypeError("The editor produced an invalid page.");
  const json = JSON.stringify({ version: FORMAT_VERSION, canvas });
  const metadata = `<metadata id="${METADATA_ID}">${escapeText(json)}</metadata>`;
  return `${rendered.slice(0, rootEnd + 1)}${metadata}${rendered.slice(rootEnd + 1)}`;
}

/**
 * The editor document embedded in a page, or `null` for a blank page. Pages with
 * artwork but no editor document were not made here and cannot be edited safely.
 */
export function canvasDocument(svg: string): CanvasDocument | null {
  const start = svg.indexOf("<metadata");
  const end = svg.indexOf(METADATA_END, start);
  if (start < 0 || end < 0) {
    const artwork = svg.slice(svg.indexOf(">") + 1, svg.lastIndexOf("</svg>"));
    if (artwork.trim() === "") return null;
    throw new TypeError("This page was not made in the leaflet editor.");
  }
  const fragment = new DOMParser().parseFromString(
    svg.slice(start, end + METADATA_END.length),
    "application/xml",
  );
  const metadata = fragment.documentElement;
  if (
    fragment.getElementsByTagName("parsererror").length > 0 ||
    metadata.localName !== "metadata" ||
    metadata.getAttribute("id") !== METADATA_ID
  )
    throw new TypeError("This page was not made in the leaflet editor.");
  const record = object(JSON.parse(metadata.textContent ?? ""));
  if (!READABLE_VERSIONS.has(record.version))
    throw new TypeError("This leaflet was saved by a newer editor. Reload the page.");
  return parseCanvas(record.canvas);
}

export function pageBytes(svg: string): number {
  return new TextEncoder().encode(svg).length;
}

/** The bitmaps that either page draws. */
export function pageBitmaps(pages: Pages): ReadonlySet<string> {
  const references = new Set<string>();
  for (const side of sides)
    for (const item of canvasDocument(pages[side])?.objects ?? [])
      if (item.type === "Image") references.add(String(item.src));
  return references;
}

function escapeText(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

const objectTypes = new Set(["Textbox", "Rect", "Ellipse", "Line", "Image", "QrCode"]);
// Patterns, clip paths and canvas images can load further content; the editor never creates them.
const unsupportedProperties = new Set([
  "source",
  "clipPath",
  "backgroundImage",
  "overlayImage",
]);

/** Saved pages are shared between organisers, so only the editor's own content is loaded. */
function parseCanvas(value: unknown): CanvasDocument {
  const canvas = object(value);
  const objects = canvas.objects;
  if (!Array.isArray(objects)) throw new TypeError("The leaflet page has no objects.");
  for (const item of objects) {
    const record = object(item);
    if (typeof record.type !== "string" || !objectTypes.has(record.type))
      throw new TypeError("The leaflet page contains unsupported content.");
    if (record.type === "Image" && !("src" in record))
      throw new TypeError("Leaflet images must refer to the branch's bitmaps.");
    if (record.type === "QrCode" && typeof record.url !== "string")
      throw new TypeError("The leaflet page contains unsupported content.");
    if ("name" in record && typeof record.name !== "string")
      throw new TypeError("The leaflet page contains unsupported content.");
  }
  checkProperties(canvas);
  return canvas as CanvasDocument;
}

function checkProperties(value: unknown, key = ""): void {
  if (unsupportedProperties.has(key))
    throw new TypeError("The leaflet page contains unsupported content.");
  if (key === "src" && !isBitmapReference(value))
    throw new TypeError("Leaflet images must refer to the branch's bitmaps.");
  if (Array.isArray(value)) for (const item of value) checkProperties(item);
  else if (value !== null && typeof value === "object")
    for (const [childKey, child] of Object.entries(value)) checkProperties(child, childKey);
}

function object(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    throw new TypeError("Expected an object");
  return value as Record<string, unknown>;
}
